import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

export type ReloadVisibility = 'data-visible' | 'data-hidden';

export interface AsyncModel {
  showData$: Observable<boolean>;
  showLoading$: Observable<boolean>;
  showAlternateLoading$: Observable<boolean>;
  showError$: Observable<boolean>;

  setLoading(b: boolean): void;

  setErrored(b: boolean): void;

  setReloadVisibility(dataVisible: ReloadVisibility): void;
}

const DEBUG = false;

@Injectable()
export class AsyncUiModel implements AsyncModel {
  private readonly loading$$ = new ReplaySubject<boolean>(1);
  private readonly reloadVisibility$$ = new BehaviorSubject<ReloadVisibility>('data-hidden');
  private readonly errored$$ = new ReplaySubject<boolean>(1);
  readonly showData$: Observable<boolean>;
  readonly showLoading$: Observable<boolean>;
  readonly showAlternateLoading$: Observable<boolean>;
  readonly showError$: Observable<boolean>;

  constructor() {
    let loading$ = this.loading$$.asObservable();
    let errored$ = this.errored$$.asObservable();
    if (DEBUG) {
      loading$ = loading$.pipe(tap((v) => console.log('loading: ', v)));
      errored$ = errored$.pipe(tap((v) => console.log('error: ', v)));
    }
    loading$ = loading$.pipe(shareReplay(1));
    errored$ = errored$.pipe(shareReplay(1));

    const reloadVis$ = this.reloadVisibility$$.pipe(distinctUntilChanged(), shareReplay(1));

    const dataLoading$ = reloadVis$.pipe(
      switchMap((vis) => {
        if (vis === 'data-visible') {
          return of(false);
        }
        return loading$;
      }),
    );
    const showData$ = combineLatest([dataLoading$, errored$.pipe(startWith(false))]).pipe(
      map(([loading, errored]) => !loading && !errored),
    );
    this.showData$ = reloadVis$.pipe(
      switchMap((vis) => {
        if (vis === 'data-hidden') {
          return showData$.pipe(startWith(false));
        }
        return showData$;
      }),
      distinctUntilChanged(),
    );
    const baseLoading$ = loading$.pipe(startWith(true), shareReplay(1));
    this.showLoading$ = reloadVis$.pipe(
      switchMap((vis) => {
        if (vis === 'data-hidden') {
          return baseLoading$;
        }
        return of(false);
      }),
      distinctUntilChanged(),
    );
    this.showAlternateLoading$ = reloadVis$.pipe(
      switchMap((vis) => {
        if (vis === 'data-hidden') {
          return of(false);
        }
        return baseLoading$;
      }),
      distinctUntilChanged(),
    );
    this.showError$ = combineLatest([errored$, loading$]).pipe(
      map(([errored, loading]) => !loading && errored),
      shareReplay(1),
      startWith(false),
      distinctUntilChanged(),
    );
  }

  setLoading(b: boolean): void {
    this.loading$$.next(b);
  }

  setErrored(b: boolean): void {
    this.errored$$.next(b);
  }

  setReloadVisibility(v: ReloadVisibility): void {
    this.reloadVisibility$$.next(v);
  }
}
